import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FirebaseAnalyticsService } from './firebase/FirebaseAnalyticsService';
import {
  TABS,
  STORY_HOME,
  STORY_OPTIONS,
  STORY_CUSTOMIZATION,
  ACCOUNT_SETTINGS,
  AUTH,
  CHANGE_PASSWORD,
  FORGOT_PASSWORD,
  EMAIL_TO_VERIFY_MODAL,
  LANDING_SCREEN,
  BRONZE_BENEFITS,
  SIGN_UP,
  SIGN_IN,
  PROFILE_AGE,
  PROFILE_CREATE,
  PROFILE_CUSTOMIZATION,
  PROFILE_GET_READY,
  PROFILE_EDIT,
  PROFILE_LANGUAGE,
  PROFILE_NARRATOR,
  PROFILE_SETTINGS,
  CHARACTER,
  CONFLICT,
  GENRE,
  GENRE_SELECTION,
  LOADING_SCREEN,
  PLOT,
  SETTING,
  STORY_PLAYER,
  STORY_PLAYER_WEB,
  THEME,
  TONE,
  WORD_OF_THE_DAY,
  PRIVACY,
  TERMS,
  ABOUT,
  TOS_PRIVACY_AGREEMENT,
  RATING_1,
  RATING_2,
  WELCOME_SCREEN_1,
  WELCOME_SCREEN_2,
  WELCOME_SCREEN_3,
  WELCOME_SCREEN_4,
  WELCOME_SCREEN_AGE,
  WELCOME_SCREEN_PAYWALL,
  WELCOME_SCREEN_DECLINE_OFFER_1,
  FAVORITE_STORIES_TAB,
  RECENT_STORIES_TAB,
} from '@storees/shared-constants';

@Injectable({
  providedIn: 'root'
})
export class RouteTrackingService {
  private router = inject(Router);
  private analytics = inject(FirebaseAnalyticsService);

  private readonly routeToScreenNameMap: Map<string, string>;

  constructor() {
    // Initialize the route-to-screen-name mapping using constants
    this.routeToScreenNameMap = new Map([
      // Tabs
      [`${TABS}/${STORY_HOME}`, 'Story Home'],
      [`${TABS}/${RECENT_STORIES_TAB}`, 'Recent Stories'],
      [`${TABS}/${FAVORITE_STORIES_TAB}`, 'Favorite Stories'],
      [`${TABS}/${AUTH}`, 'Authentication'],
      [TABS, 'Tabs'],

      // Onboarding
      [LANDING_SCREEN, 'Landing Screen'],
      [WELCOME_SCREEN_1, 'Welcome Screen 1'],
      [WELCOME_SCREEN_2, 'Welcome Screen 2'],
      [WELCOME_SCREEN_3, 'Welcome Screen 3'],
      [WELCOME_SCREEN_4, 'Welcome Screen 4'],
      [WELCOME_SCREEN_PAYWALL, 'Welcome Screen Paywall'],
      [WELCOME_SCREEN_DECLINE_OFFER_1, 'Welcome Screen Decline Offer'],
      [WELCOME_SCREEN_AGE, 'Age Selection'],
      [TOS_PRIVACY_AGREEMENT, 'Terms Agreement'],
      [TERMS, 'Terms of Service'],
      [PRIVACY, 'Privacy Policy'],
      [ABOUT, 'About'],

      // Auth
      [SIGN_UP, 'Sign Up'],
      [SIGN_IN, 'Sign In'],
      [FORGOT_PASSWORD, 'Forgot Password'],
      [CHANGE_PASSWORD, 'Change Password'],
      [EMAIL_TO_VERIFY_MODAL, 'Email Verification'],
      [AUTH, 'Authentication'],

      // Story Creation Flow
      [GENRE, 'Story Type'],
      [GENRE_SELECTION, 'Genre Selection'],
      [SETTING, 'Setting Selection'],
      [THEME, 'Theme Selection'],
      [CONFLICT, 'Conflict Selection'],
      [PLOT, 'Plot Selection'],
      [CHARACTER, 'Character Selection'],
      [TONE, 'Tone Selection'],
      [WORD_OF_THE_DAY, 'Word of the Day'],
      [LOADING_SCREEN, 'Story Loading'],
      [STORY_PLAYER, 'Story Player'],
      [STORY_PLAYER_WEB, 'Story Player (Web)'],
      [STORY_OPTIONS, 'Story Options'],
      [STORY_CUSTOMIZATION, 'Story Customization'],

      // Profile Management
      [PROFILE_CREATE, 'Profile Create'],
      [PROFILE_EDIT, 'Profile Edit'],
      [PROFILE_AGE, 'Profile Age'],
      [PROFILE_NARRATOR, 'Profile Narrator'],
      [PROFILE_LANGUAGE, 'Profile Language'],
      [PROFILE_CUSTOMIZATION, 'Profile Customization'],
      [PROFILE_GET_READY, 'Profile Get Ready'],
      [PROFILE_SETTINGS, 'Profile Settings'],

      // Settings & Account
      [ACCOUNT_SETTINGS, 'Account Settings'],
      [BRONZE_BENEFITS, 'Bronze Benefits'],

      // Rating
      [RATING_1, 'Rating Step 1'],
      [RATING_2, 'Rating Step 2'],
    ]);
  }

  async startTracking() {
    // Log the current/initial route immediately
    const currentUrl = this.router.url;
    console.log(`Initial route: ${currentUrl}`);
    const screenName = this.getScreenName(currentUrl);
    console.log(`Initial Screen Name: ${screenName}`);
    await this.analytics.logScreenView(screenName);

    // Then subscribe to future navigation events
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(async (event: NavigationEnd) => {
        console.log(`Navigated to: ${event.urlAfterRedirects}`);
        const screenName = this.getScreenName(event.urlAfterRedirects);
        console.log(`Screen Name: ${screenName}`);
        await this.analytics.logScreenView(screenName);
      });
  }

  private getScreenName(url: string): string {
    // Remove leading slash and query parameters
    const cleanUrl = url.split('?')[0].replace(/^\//, '');

    // Try to find exact match in the map
    if (this.routeToScreenNameMap.has(cleanUrl)) {
      return this.routeToScreenNameMap.get(cleanUrl) ?? 'Home';
    }

    // Try to find partial matches for nested routes (e.g., tabs/story-home)
    for (const [route, screenName] of this.routeToScreenNameMap.entries()) {
      if (cleanUrl === route || cleanUrl.endsWith(`/${route}`)) {
        return screenName;
      }
    }

    // Fallback formatting for unmapped routes
    return this.formatUnknownRoute(cleanUrl);
  }

  private formatUnknownRoute(url: string): string {
    // Fallback formatting for unmapped routes
    if (!url || url === '') return 'Home';

    // Get the last segment of the URL
    const segments = url.split('/');
    const lastSegment = segments[segments.length - 1];

    // Convert kebab-case to Title Case
    return lastSegment
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
